import React, { useEffect, useRef } from "react"
import { Chart, ArcElement, Tooltip } from "chart.js"
import type { ExposureSlice } from "~/api/portfolio_summaries"
import { CHART_PALETTE, formatCurrency } from "./utils"

Chart.register(ArcElement, Tooltip)

interface ExposurePieChartProps {
  data: ExposureSlice[]
  maxSlices?: number
}

const ExposurePieChart: React.FC<ExposurePieChartProps> = ({
  data,
  maxSlices = 10,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart<"doughnut"> | null>(null)

  const sorted = [...data].filter((d) => d.mv > 0).sort((a, b) => b.mv - a.mv)
  const topItems = sorted.slice(0, maxSlices)
  const rest = sorted.slice(maxSlices)
  const restTotal = rest.reduce((sum, d) => sum + d.mv, 0)
  const chartData =
    restTotal > 0
      ? [...topItems, { label: `${rest.length} others`, mv: restTotal }]
      : topItems
  const total = chartData.reduce((sum, d) => sum + d.mv, 0)

  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return

    if (chartInstanceRef.current) chartInstanceRef.current.destroy()

    const ctx = chartRef.current.getContext("2d")
    if (!ctx) return

    chartInstanceRef.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: chartData.map((d) => d.label),
        datasets: [
          {
            data: chartData.map((d) => d.mv),
            backgroundColor: chartData.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]),
            borderColor: "#FFFFFF",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "58%",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => {
                const v = c.parsed as number
                return ` ${c.label}: ${formatCurrency(v)} (${((v / total) * 100).toFixed(1)}%)`
              },
            },
          },
        },
      },
    })

    return () => {
      chartInstanceRef.current?.destroy()
      chartInstanceRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(chartData)])

  if (chartData.length === 0) {
    return (
      <div className="b-portfolio-summary-chart-empty">
        No exposure data available for this period.
      </div>
    )
  }

  return (
    <div className="b-portfolio-summary-pie">
      <div className="b-portfolio-summary-chart-wrap">
        <canvas ref={chartRef} />
      </div>
      <div className="b-portfolio-summary-legend">
        {chartData.map((d, i) => (
          <span className="b-portfolio-summary-legend-item" key={d.label}>
            <span
              className={`b-portfolio-summary-legend-swatch b-portfolio-summary-color-${
                i % CHART_PALETTE.length
              }`}
            />
            {d.label}{" "}
            <span className="b-portfolio-summary-legend-val">
              {((d.mv / total) * 100).toFixed(1)}%
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

export default ExposurePieChart
